import { nth, reduce, transform, uniq } from 'lodash-es';
import type { AnyObject, InferType, ObjectSchema, ValidationError } from 'yup-v1-latest';
import { EMPTY_OBJECT } from './empty';

const pathPartsRegex = /(\.?\[?[^.[\]]+\]?)/g;

type DescribedObjectSchema = {
  fields?: Record<string, { optional?: boolean }>;
};

export type RequiredFieldsMap = Record<string, boolean>;

export type MultipleErrorMode = 'multiple' | 'inline';

export type RunValidationV1Options = {
  /** Used to format multiple errors for a single field. Defaults to `'multiple'` (newline). */
  multipleErrorMode?: MultipleErrorMode;
};

function formatFieldErrors(messages: string[], options?: RunValidationV1Options): string {
  const mode = options?.multipleErrorMode ?? 'multiple';
  const separator = mode === 'inline' ? '; ' : '\n';
  return uniq(messages).join(separator);
}

/**
 * Returns a top-level required-field map from a Yup v1 object schema, resolving `.when()` branches with the provided data.
 */
export function getRequiredFieldsV1<T extends ObjectSchema<AnyObject, any>>(
  schema: T,
  data?: AnyObject
): RequiredFieldsMap {
  const description = schema.describe({ value: data ?? EMPTY_OBJECT }) as DescribedObjectSchema;
  const fields = description.fields ?? EMPTY_OBJECT;

  return Object.entries(fields).reduce<RequiredFieldsMap>((requiredFields, [fieldName, fieldDescription]) => {
    requiredFields[fieldName] = fieldDescription.optional === false;
    return requiredFields;
  }, {});
}

/**
 * Runs validation using yup-v1-latest (Yup v1) schema (errors returned only)
 * @param schema - The yup schema to validate against
 * @param data - The data to validate
 * @param options - Optional validation behavior; `multipleErrorMode: 'inline'` joins same-field errors with `'; '`
 *   (defaults to `'multiple'`, newline-separated)
 * @returns Object with validation errors keyed by field path
 */
export function runValidationV1<T extends ObjectSchema<AnyObject, any>>(
  schema: T,
  data: AnyObject,
  options?: RunValidationV1Options
): { [key: string]: string } {
  const validationRun = runValidationV1WithResult(schema, data, options);
  return validationRun.result === 'success' ? EMPTY_OBJECT : validationRun.errors;
}

/**
 * Runs validation using yup-v1-latest (Yup v1) schema
 * @param schema - The yup schema to validate against
 * @param data - The data to validate
 * @param options - Optional validation behavior; `multipleErrorMode: 'inline'` joins same-field errors with `'; '`
 *   (defaults to `'multiple'`, newline-separated)
 * @returns Object with validated and inferred schema result, and errors
 */
export function runValidationV1WithResult<T extends ObjectSchema<AnyObject, any>>(
  schema: T,
  data: AnyObject,
  options?: RunValidationV1Options
):
  | {
      result: 'success';
      validatedData: InferType<T>;
    }
  | {
      result: 'failure';
      errors: { [key: string]: string };
    } {
  try {
    const validation = schema.validateSync(data, { abortEarly: false });
    return {
      result: 'success',
      validatedData: validation,
    };
  } catch (err: unknown) {
    /*
  When we validate the objects in an array, yup emits errors with a path like `subAccountAllocations[0].subAccount`, which isn't what we want
  This code takes that path, breaks it up into the various "parts", and adds the error message to each of those keys.
  The result of this is that errors would look something like this:
  ```
  {
    "subAccountAllocations": ["Sub Account is required", "Sub Account is required", "Value must be > 0"],
    "subAccountAllocations[0]": ["Sub Account is required"],
    "subAccountAllocations[0].subAccount": ["Sub Account is required"],
    "subAccountAllocations[1]": ["Sub Account is required", "Value must be > 0"],
    "subAccountAllocations[1].subAccount": ["Sub Account is required"],
    "subAccountAllocations[1].value": ["Value must be > 0"],
  }
  ```
*/
    const errors = ((err as ValidationError).inner ?? []).reduce(
      (allErrors, innerError) => {
        const pathParts = (innerError.path ?? '').match(pathPartsRegex) ?? [];
        const paths = reduce(
          pathParts,
          (paths, currentSegment) => [...paths, `${nth(paths, -1) ?? ''}${currentSegment}`],
          [] as string[]
        );
        paths.forEach(path => {
          allErrors[path] = [...(allErrors[path] ?? []), innerError.message];
        });
        return allErrors;
      },
      {} as { [key: string]: string[] }
    );
    /*
  We now take that errors object, and concatenate all the unique errors per key,
  to create a summary of the errors for each key with its children
  The result of this is that errors would look something like this:
  ```
  {
    "subAccountAllocations": "Sub Account is required\nValue must be > 0",
    "subAccountAllocations[0]": "Sub Account is required",
    "subAccountAllocations[0].subAccount": "Sub Account is required",
    "subAccountAllocations[1]": "Sub Account is required\nValue must be > 0",
    "subAccountAllocations[1].subAccount": "Sub Account is required",
    "subAccountAllocations[1].value": "Value must be > 0",
  }
  ```
*/
    const errorsResult = transform<string[], Record<string, string>>(
      errors,
      (allErrors, thisError, key) => (allErrors[key] = formatFieldErrors(thisError, options)),
      {}
    );
    return {
      result: 'failure',
      errors: errorsResult,
    };
  }
}
